/**
 * Bacteria — the thing that lives on Level 0.
 *
 * Lore: the entity from Kane Pixels' found footage, catalogued in the game
 * wikis as Bacteria ("The Backrooms Monster", "Wired"). Every description
 * agrees on the same figure: a towering black humanoid that looks assembled
 * out of wire or rebar, with limbs far too long, joints that fold the wrong
 * way, arms that reach the floor, and no face of any kind. It can hold
 * perfectly still, and it screams before it runs.
 *
 * So it is built from struts and nothing else. There is no skin here, no
 * surface: every part of it is a bundle of twisted wires with air between
 * them, which is why it is so hard to resolve at the far end of a corridor —
 * there is barely anything there to catch the light — and so bad to be near,
 * where you can see how many separate pieces of wire it is.
 */

import * as THREE from 'three';
import { clamp01, damp, lerp, noise1, renderYaw, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { EntityView } from './api';
import { castShadows, chain, dampAngle, freeAll, Kit, mergeParts, planarSpeed, type Disposable } from './entkit';

/* ------------------------------------------------------------------ *
 * Wire
 * ------------------------------------------------------------------ */

/** Twelve millimetres. Thin enough to vanish at range, thick enough to bite. */
const GAUGE = 0.012;
/** Segments along one wire. Three-sided tubes; a wire has no far side. */
const SPAN = 4;
const SIDES = 3;

/**
 * One wire: a helix down the limb axis, hanging from y=0 like `bone` does so
 * the standard joint chain can carry it.
 *
 * `spread` is the radius the bundle opens to at the bottom. Setting it wider
 * than the top is how the forearms end in fingers — the strands simply stop
 * travelling together, and three wires that have come apart read as a hand
 * without a hand having to be modelled.
 */
function wire(length: number, top: number, spread: number, phase: number, twist: number, bow: number): THREE.BufferGeometry {
  const points: THREE.Vector3[] = [];
  for (let i = 0; i <= SPAN; i++) {
    const t = i / SPAN;
    const a = phase + t * twist;
    const r = lerp(top, spread, t * t);
    points.push(new THREE.Vector3(Math.cos(a) * r + bow * t * t, -t * length, Math.sin(a) * r));
  }
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), SPAN, GAUGE, SIDES, false);
}

/** A bundle of wires travelling roughly together. One draw call, no surface. */
function bundle(
  length: number,
  top: number,
  spread: number,
  count: number,
  twist: number,
  rng: { range(a: number, b: number): number }
): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < count; i++) {
    parts.push(
      wire(
        length * rng.range(0.94, 1.03),
        top * rng.range(0.7, 1.2),
        spread * rng.range(0.75, 1.25),
        (i / count) * TAU + rng.range(-0.3, 0.3),
        twist * rng.range(0.7, 1.3),
        rng.range(-0.02, 0.02)
      )
    );
  }
  return mergeParts(parts);
}

/**
 * The head: four arcs over an ovoid and nothing inside them. A cage where a
 * face should be reads worse than a blank one, because the eye keeps trying to
 * find the face through the gaps.
 */
function makeHeadCage(rng: { range(a: number, b: number): number }): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI + rng.range(-0.15, 0.15);
    const points: THREE.Vector3[] = [];
    for (let j = 0; j <= SPAN; j++) {
      const t = (j / SPAN) * Math.PI;
      const r = Math.sin(t) * rng.range(0.075, 0.098);
      points.push(new THREE.Vector3(Math.cos(a) * r, 0.09 - Math.cos(t) * 0.115, Math.sin(a) * r * 0.82));
    }
    parts.push(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), SPAN, GAUGE * 0.85, SIDES, false));
  }
  return mergeParts(parts);
}

/* ------------------------------------------------------------------ *
 * Kit
 * ------------------------------------------------------------------ */

/**
 * Hips. Set a little above the leg bones' own length so the splayed wire ends
 * that pass for feet rest on the carpet instead of through it. It is built
 * upright and spends most of its time folded over.
 */
const HIP_Y = 1.29;
const THIGH = 0.64;
const SHIN = 0.62;
const TORSO = 0.6;
const UPPER_ARM = 0.86;
/** The forearms are longer than the upper arms. That is the wrong way round. */
const FOREARM = 0.9;

interface BacteriaRig {
  root: THREE.Group;
  torso: THREE.Group;
  neck: THREE.Group;
  head: THREE.Group;
  legs: THREE.Group[][];
  arms: THREE.Group[][];
}

interface BacteriaKit extends Disposable {
  make(): BacteriaRig;
}

function buildBacteriaKit(): BacteriaKit {
  // Not quite black. Pure black is a hole in the screen; this is dark enough
  // to be a silhouette in the room lights and to show as wire in a torch beam.
  const material = new THREE.MeshLambertMaterial({ color: 0x0b0b0e });

  // One fixed seed, so every Bacteria is the same Bacteria. There is only ever
  // supposed to be one of these on a level anyway.
  const rng = makeRNG(0x2c19f5);
  const thighGeo = bundle(THIGH, 0.05, 0.032, 3, 1.7, rng);
  const shinGeo = bundle(SHIN, 0.032, 0.018, 3, 1.4, rng);
  const upperGeo = bundle(UPPER_ARM, 0.042, 0.026, 3, 1.9, rng);
  // Splayed at the far end: the three wires separate and become fingers.
  const foreGeo = bundle(FOREARM, 0.026, 0.062, 3, 0.7, rng);
  const torsoGeo = bundle(TORSO, 0.1, 0.075, 5, 1.2, rng);
  const neckGeo = bundle(0.16, 0.035, 0.045, 3, 1.1, rng);
  const headGeo = makeHeadCage(rng);

  const geos = [thighGeo, shinGeo, upperGeo, foreGeo, torsoGeo, neckGeo, headGeo];
  const legLens = [THIGH, SHIN];
  const armLens = [UPPER_ARM, FOREARM];

  return {
    make(): BacteriaRig {
      const root = new THREE.Group();

      // The torso hangs upward from the hips: one bundle, inverted, so the
      // shoulders are the far end of a limb rather than a joint of their own.
      const torso = new THREE.Group();
      torso.position.y = HIP_Y;
      const trunk = new THREE.Mesh(torsoGeo, material);
      trunk.rotation.x = Math.PI;
      torso.add(trunk);
      root.add(torso);

      const neck = new THREE.Group();
      neck.position.y = TORSO;
      const throat = new THREE.Mesh(neckGeo, material);
      throat.rotation.x = Math.PI;
      neck.add(throat);
      torso.add(neck);

      const head = new THREE.Group();
      head.position.y = 0.16;
      head.add(new THREE.Mesh(headGeo, material));
      neck.add(head);

      const legs: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain([thighGeo, shinGeo], legLens, material);
        joints[0]!.position.set(side * 0.11, HIP_Y, 0);
        root.add(joints[0]!);
        legs.push(joints);
      }

      const arms: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain([upperGeo, foreGeo], armLens, material);
        joints[0]!.position.set(side * 0.19, TORSO - 0.04, 0);
        joints[0]!.rotation.z = side * 0.08;
        torso.add(joints[0]!);
        arms.push(joints);
      }

      castShadows(root);
      return { root, torso, neck, head, legs, arms };
    },
    dispose(): void {
      freeAll(geos);
      material.dispose();
    },
  };
}

const bacteriaKit = new Kit(buildBacteriaKit);

/* ------------------------------------------------------------------ *
 * View
 * ------------------------------------------------------------------ */

/**
 * A held twitch rather than a per-frame buzz. Value noise sampled fast still
 * holds each value for a few frames, which is the difference between a thing
 * that is shaking and a thing that keeps arriving in a slightly new position.
 */
function tremor(time: number, seed: number, n: number): number {
  return noise1(time * 9 + n, seed + n * 37) - 0.5;
}

/**
 * Build the Bacteria. Two metres and change of black wire that walks folded
 * over, stands up to scream, and then comes at you faster than it should.
 */
export function createBacteriaView(): EntityView {
  const kit = bacteriaKit.acquire();
  const rig = kit.make();
  const object = new THREE.Group();
  object.add(rig.root);

  // Cosmetic per-instance offsets so two never twitch in time.
  const seed = (Math.random() * 0xffff) | 0;
  const phase = Math.random() * TAU;
  let stride = phase;
  let yaw = 0;
  let yawInit = false;
  let run = 0;
  let rear = 0;
  let dead = 0;

  return {
    object,
    update(state, dt, time, _camera) {
      if (!yawInit) {
        yaw = state.yaw;
        yawInit = true;
      }
      // Turns fast. Nothing about how it moves is committed to the last
      // direction it was going.
      yaw = dampAngle(yaw, state.yaw, 7.5, dt);
      object.position.set(state.pos.x, 0, state.pos.z);
      object.rotation.y = renderYaw(yaw);

      const speed = planarSpeed(state);
      const drive = clamp01(speed / 5);
      stride += dt * (1.4 + speed * 2.6);
      run = damp(run, drive, 4, dt);
      // It stands up to its full height only when it has seen you and has not
      // started moving yet. That pose is the warning, and it is brief.
      rear = damp(rear, state.awareness > 0.55 && speed < 0.6 && state.mode !== 'dead' ? 1 : 0, 5, dt);
      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 2.2, dt);
      const alive = 1 - dead;

      // Two clocks of noise, not random: a fine tremor that holds for a few
      // frames at a time, and a slow gate that stops it dead. A wanderer who
      // looks away and back has no way to tell whether it moved.
      const gate = (noise1(time * 0.29, seed + 11) > 0.68 ? 0 : 1) * alive;
      const twitch = (0.05 + run * 0.09) * gate;

      const swing = 0.18 + run * 0.95;
      for (let i = 0; i < 2; i++) {
        const p = stride + i * Math.PI;
        const leg = rig.legs[i]!;
        leg[0]!.rotation.x = Math.sin(p) * swing * alive + tremor(time, seed, i) * twitch;
        // The knee folds forward. There is no arrangement of a human leg that
        // does this, and it is the first thing that is wrong about the walk.
        leg[1]!.rotation.x = Math.max(0, Math.sin(p + 0.6)) * swing * 1.5 * alive + 0.05;
        leg[0]!.rotation.z = (i === 0 ? -1 : 1) * (0.06 + run * 0.16);

        const arm = rig.arms[i]!;
        // Hanging when it walks, trailing behind it when it runs, and never
        // used for balance. The wrist bends back on itself.
        arm[0]!.rotation.x = Math.sin(p + Math.PI) * swing * 0.3 * alive - run * 0.5 + rear * 0.35;
        arm[0]!.rotation.z = (i === 0 ? -1 : 1) * (0.08 + rear * 0.5 + tremor(time, seed, i + 5) * twitch);
        arm[1]!.rotation.x =
          -0.12 - run * 0.55 + Math.sin(p + 2.1) * 0.1 * alive + tremor(time, seed, i + 9) * twitch * 2;
      }

      // Folded almost double at a run, straight up when it screams, and a
      // shallow forward hunch the rest of the time.
      rig.torso.rotation.x = 0.34 + run * 0.42 - rear * 0.38 + tremor(time, seed, 3) * twitch;
      rig.torso.rotation.z = Math.sin(stride) * 0.05 * run + tremor(time, seed, 4) * twitch;
      rig.neck.rotation.x = -0.2 + run * 0.5 - rear * 0.95;
      rig.head.rotation.x = -0.15 - rear * 0.55 + tremor(time, seed, 6) * twitch * 3;
      rig.head.rotation.y = tremor(time, seed, 7) * twitch * 6;
      rig.head.rotation.z = tremor(time, seed, 8) * twitch * 4;

      rig.root.position.y = Math.abs(Math.sin(stride)) * 0.035 * run - dead * 0.55;
      rig.root.rotation.z = dead * 1.5;
      rig.root.rotation.x = -run * 0.06;
    },
    dispose() {
      object.clear();
      bacteriaKit.release();
    },
  };
}
